import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, MenuProps, Tooltip, Typography } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  DownOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { TokenService } from '../../services/secureTokenService';
import { AccountType } from '../../enums/account';
import './AccountDropdown.less';
import AccountItem from './AccountItem';
import { Auth0UserInfo } from '../../interfaces/account';
import { AppRoutes } from '../../constants/route';
import { AppDispatch, RootState } from '../../app/store';
import { Team, TeamWithSubTeams } from '../../interfaces/team';
import { selectAccount } from '../../app/ActionTypes/teamActionTypes';
import { selectIsTeamOnlyWithNoTeams } from '../../app/Slices/teamSlice';
import { useRouting } from '../../hooks/useRouting';
import { useNavigate } from 'react-router-dom';
import { navigateTo } from '../../utils/routeUtils';
import { groupTeams } from '../../services/account.service';

type MenuClickEvent = Parameters<NonNullable<MenuProps['onClick']>>[0];

const AccountDropdown: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { homePath } = useRouting();
  const {
    allTeams: allTeamsInfo,
    currentTeam: currentTeamInfo,
    personalAccount: personalAccountInfo,
  } = useSelector((state: RootState) => state.team);
  const isTeamOnly = personalAccountInfo.record?.team_only === true;
  const [selectedAccountType, setSelectedAccountType] = useState<AccountType>(
    AccountType.Personal
  );
  const [selectedAccount, setSellectedAccount] = useState<
    Auth0UserInfo | Team | null
  >(null);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);
  const [userInfo, setUserInfo] = useState<Auth0UserInfo | null>(null);

  const groupedTeams = useMemo(() => {
    return groupTeams(allTeamsInfo.records);
  }, [allTeamsInfo.records]);

  useEffect(() => {
    // Get user info once and store it
    const info = TokenService.getUserInfo();
    setUserInfo(info);
  }, []);

  useEffect(() => {
    if (allTeamsInfo.loading) {
      return;
    }
    if (currentTeamInfo.record) {
      const _team = currentTeamInfo.record;
      setSelectedAccountType(AccountType.Team);
      setSellectedAccount(_team);
      setIsLoadingUserInfo(false);
    } else {
      setSelectedAccountType(AccountType.Personal);
      if (userInfo) {
        setSellectedAccount(userInfo);
        setIsLoadingUserInfo(false);
      } else {
        // If userInfo is not available yet, keep the current account to prevent dropdown from disappearing
        // This handles the case where tokens are still loading
        setIsLoadingUserInfo(true);
      }
    }
  }, [currentTeamInfo.record, allTeamsInfo.loading, userInfo]);

  const handleAccountSwitch = useCallback(
    async (type: AccountType, teamId?: string) => {
      try {
        const selectedTeam =
          type === AccountType.Team
            ? allTeamsInfo.records.find((_team) => _team.id === teamId) || null
            : null;

        // Update backend and refresh data
        await dispatch(selectAccount(selectedTeam));

        // Navigate after successful switch
        navigate(homePath);
      } catch (error) {
        console.error('Failed to switch account:', error);
      }
    },
    [allTeamsInfo.records, dispatch, navigate, homePath]
  );

  const generateHeaderItem = useCallback((type: AccountType) => {
    const isPersonal = type === AccountType.Personal;
    return {
      key: `header-${type}`,
      label: (
        <div className="account-dropdown-section-header">
          {isPersonal ? <UserOutlined /> : <TeamOutlined />}
          <Typography.Text>{isPersonal ? 'Personal' : 'Teams'}</Typography.Text>
        </div>
      ),
      disabled: true,
    };
  }, []);

  const hasTeams = allTeamsInfo.records.length > 0;

  const isTeamOnlyWithNoTeams = useSelector(selectIsTeamOnlyWithNoTeams);

  const dropdownMenu = useMemo<MenuProps['items']>(() => {
    if (!selectedAccount) return [];

    const items = [];
    // Personal section (hidden for team-only users)
    if (!isTeamOnly) {
      items.push(
        ...[
          generateHeaderItem(AccountType.Personal),
          {
            key: 'personal',
            label: (
              <div className="account-dropdown-item">
                <AccountItem
                  type={AccountType.Personal}
                  object={userInfo || ({} as Auth0UserInfo)}
                />
                {selectedAccountType === AccountType.Personal && (
                  <CheckOutlined className="account-dropdown-check" />
                )}
              </div>
            ),
            onClick: () => handleAccountSwitch(AccountType.Personal),
          },
        ]
      );
    }

    // Organizations section
    if (hasTeams) {
      const teamAccountItems: MenuProps['items'] = [];
      const genTeamAccountItemOptions = (team: TeamWithSubTeams) => {
        const isAccessible = Boolean(team.current_user);
        return {
          key: `org-${team.id}`,
          disabled: !isAccessible,
          label: (
            <Tooltip
              title={!isAccessible && 'You do not have access to this team'}
            >
              <div
                className="account-dropdown-item"
                // style={{ marginLeft: team.master_organization_id ? 30 : 0 }}
              >
                <AccountItem
                  className={
                    team.master_organization_id
                      ? 'account-sub-item'
                      : 'account-master-item'
                  }
                  // className="account-sub-item"
                  type={AccountType.Team}
                  object={team}
                  showLicense
                  showRole
                  disabled={!isAccessible}
                />
                {selectedAccountType === AccountType.Team &&
                  (selectedAccount as Team).id === team.id && (
                    <CheckOutlined className="account-dropdown-check" />
                  )}
              </div>
            </Tooltip>
          ),
          onClick: () => handleAccountSwitch(AccountType.Team, team.id),
        };
      };

      groupedTeams.forEach((team) => {
        const _item = genTeamAccountItemOptions(team);
        teamAccountItems.push(_item);

        if (team.subTeams) {
          team.subTeams.forEach((subTeam) => {
            const _subitem = genTeamAccountItemOptions(subTeam);
            teamAccountItems.push(_subitem);
          });
        }
      });

      items.push(
        ...[
          // Only show divider between personal and teams sections
          ...(!isTeamOnly
            ? [{ key: 'divider', type: 'divider' as const }]
            : []),
          generateHeaderItem(AccountType.Team),
          ...teamAccountItems,
        ]
      );
    }

    items.push(
      ...[
        {
          key: 'divider-2',
          type: 'divider' as const,
        },
        {
          key: `team-create`,
          label: (
            <div className="account-dropdown-item link">Create New Team</div>
          ),
          onClick: (e: MenuClickEvent) => {
            navigateTo({
              url: AppRoutes.teamsSettingsCreate.getUrl(),
              clickEvent: e.domEvent,
              navigate,
            });
          },
        },
        {
          key: `team-list`,
          label: (
            <Tooltip title={!hasTeams ? 'You don’t have any teams yet' : ''}>
              <div
                className={`account-dropdown-item link ${!hasTeams ? 'disabled' : ''}`}
              >
                View All Teams
              </div>
            </Tooltip>
          ),
          disabled: !hasTeams,
          onClick: hasTeams
            ? (e: MenuClickEvent) => {
                navigateTo({
                  url: AppRoutes.teamsSettings.getUrl(),
                  clickEvent: e.domEvent,
                  navigate,
                });
              }
            : undefined,
        },
      ]
    );
    return items;
  }, [
    selectedAccount,
    selectedAccountType,
    allTeamsInfo.records,
    userInfo,
    handleAccountSwitch,
    hasTeams,
    generateHeaderItem,
    navigate,
    isTeamOnly,
  ]);

  if (isTeamOnlyWithNoTeams) {
    return null;
  }

  // Show dropdown if we have a selected account OR if we're loading user info (to prevent disappearing)
  const shouldShowDropdown =
    selectedAccount ||
    (selectedAccountType === AccountType.Personal && isLoadingUserInfo);

  return (
    <div className="account-dropdown-wrapper">
      {shouldShowDropdown && (
        <Dropdown
          menu={{
            items: dropdownMenu,
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <div className="account-dropdown-trigger">
            <div className="account-dropdown-trigger-content">
              {selectedAccount ? (
                <AccountItem
                  type={selectedAccountType}
                  object={selectedAccount as Auth0UserInfo | Team}
                  showType
                  showLicense={false}
                  showRole={false}
                  showEmail={selectedAccountType !== AccountType.Personal}
                />
              ) : (
                <div className="account-dropdown-loading">Loading...</div>
              )}
              <DownOutlined className="account-dropdown-arrow" />
            </div>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

export default AccountDropdown;
